import {
	RiCloseCircleLine,
	RiEyeLine,
	RiFileCopyLine,
	RiMoreFill,
	RiRefreshLine,
} from '@remixicon/react';
import type { ReactNode } from 'react';

import { useClipboard } from '@shared/hooks/clipboard';
import Link from '@shared/router/link';
import * as history from '@shared/router/location';

import { Card } from '@playbook/atoms/card';
import { IconButton } from '@playbook/atoms/icon-button';
import { MenuDivider } from '@playbook/atoms/menu-divider';
import { MenuItem } from '@playbook/atoms/menu-item';
import { ContextMenu } from '@playbook/modules/context-menu';

import { Action, type Key } from '@portal/pkg/api/types';
import * as routes from '@portal/pkg/routes';
import {
	KeyStatusBadge,
	can,
	formatDate,
	formatDateTime,
} from '@portal/views/keys/_shared';

import css from './KeyRow.module.css';

/**
 *	One key in the listing.
 *
 *	The card is a plain surface rather than a link: the actions menu inside it
 *	would otherwise be a button inside an anchor. The name carries the link.
 */
export function KeyRow({ forKey }: { forKey: Key }): ReactNode {
	return (
		<Card>
			<div className={css.row}>
				<div className={css.lead}>
					<div className={css.title}>
						<Link className={css.name} href={routes.Key(forKey.id)}>
							{forKey.name}
						</Link>
						<KeyStatusBadge forKey={forKey} />
					</div>
					<code className={css.id}>{forKey.id}</code>
					{forKey.rotatedFrom && (
						<p className={css.replaced}>
							Replaced a key created {formatDate(forKey.rotatedFrom.createdAt)}
						</p>
					)}
				</div>
				<p className={css.meta}>{expiryLine(forKey)}</p>
				<KeyActions forKey={forKey} />
			</div>
		</Card>
	);
}

function expiryLine(key: Key): string {
	if (key.revokedAt) {
		return `Revoked ${formatDate(key.revokedAt)}`;
	}

	if (key.status === 'expired') {
		return `Expired ${formatDateTime(key.expiresAt)}`;
	}

	return `Expires ${formatDateTime(key.expiresAt)}`;
}

function KeyActions({ forKey }: { forKey: Key }): ReactNode {
	const { copy } = useClipboard();

	const mayRotate = can(forKey, Action.RotateKey);
	const mayRevoke = can(forKey, Action.RevokeKey);

	return (
		<ContextMenu
			label={`Actions for ${forKey.name}`}
			appearFrom="bottomRight"
			renderTriggerWith={(popoverTarget) => (
				<IconButton
					variant="ghost"
					size="small"
					label={`Actions for ${forKey.name}`}
					icon={<RiMoreFill />}
					popoverTarget={popoverTarget}
				/>
			)}>
			<MenuItem
				label="Copy key ID"
				leadingIcon={<RiFileCopyLine />}
				onClick={() => void copy(forKey.id)}
			/>
			<MenuItem
				label="View details"
				leadingIcon={<RiEyeLine />}
				onClick={() => history.push(routes.Key(forKey.id))}
			/>
			{(mayRotate || mayRevoke) && <MenuDivider />}
			{mayRotate && (
				<MenuItem
					label="Rotate key…"
					leadingIcon={<RiRefreshLine />}
					onClick={() => history.push(routes.KeyRotate(forKey.id))}
				/>
			)}
			{mayRevoke && (
				<MenuItem
					variant="caution"
					label="Revoke key…"
					leadingIcon={<RiCloseCircleLine />}
					onClick={() => history.push(routes.KeyRevoke(forKey.id))}
				/>
			)}
		</ContextMenu>
	);
}
